import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { getLastKnownAuthUser } from "./authService";


export interface UserNameSetting {
  id: number;
  name: string;
  is_favorite: boolean;
  created_at: string;
}


const NAMES_CACHE_PREFIX = "materialentnahme:user-names:v1:";

function namesCacheKey(): string | null {
  const user = getLastKnownAuthUser();
  return user ? `${NAMES_CACHE_PREFIX}${user.id}` : null;
}

export function getCachedUserNames(): UserNameSetting[] {
  const key = namesCacheKey();
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is UserNameSetting => Boolean(
      item && typeof item === "object"
      && typeof (item as UserNameSetting).id === "number"
      && typeof (item as UserNameSetting).name === "string"
      && typeof (item as UserNameSetting).is_favorite === "boolean"
    ));
  } catch {
    return [];
  }
}

export function cacheUserNames(names: UserNameSetting[]): void {
  const key = namesCacheKey();
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(names)); } catch { /* Cache ist Best-Effort. */ }
}

export interface EmailRecipientSetting {
  id: number;
  email: string;
  created_at: string;
}

const EMAIL_RECIPIENT_CACHE_PREFIX = "materialentnahme:email-recipients:v1:";

function emailRecipientCacheKey(): string | null {
  const user = getLastKnownAuthUser();
  return user ? `${EMAIL_RECIPIENT_CACHE_PREFIX}${user.id}` : null;
}

export function getCachedEmailRecipients(): EmailRecipientSetting[] | null {
  const key = emailRecipientCacheKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((item): item is EmailRecipientSetting => Boolean(
      item && typeof item === "object"
      && typeof (item as EmailRecipientSetting).id === "number"
      && typeof (item as EmailRecipientSetting).email === "string"
      && typeof (item as EmailRecipientSetting).created_at === "string"
    ));
  } catch {
    return null;
  }
}

export function cacheEmailRecipients(recipients: EmailRecipientSetting[]): void {
  const key = emailRecipientCacheKey();
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(recipients)); } catch { /* Cache ist Best-Effort. */ }
}

async function jsonOrError<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    let detail = fallback;
    try {
      const data = await response.json() as { detail?: unknown };
      if (typeof data.detail === "string" && data.detail.trim()) detail = data.detail;
    } catch {
      // Fallback bleibt erhalten.
    }
    throw new Error(detail);
  }
  return await response.json() as T;
}

export async function fetchUserNames(): Promise<UserNameSetting[]> {
  try {
    const names = await jsonOrError<UserNameSetting[]>(
      await fetchWithTimeout("/api/settings/names", { credentials: "include", cache: "no-store" }, 1800),
      "Namen konnten nicht geladen werden."
    );
    cacheUserNames(names);
    return names;
  } catch (error) {
    const cached = getCachedUserNames();
    if (cached.length > 0) return cached;
    throw error;
  }
}

export async function createUserName(name: string): Promise<UserNameSetting> {
  return await jsonOrError<UserNameSetting>(
    await fetch("/api/settings/names", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    }),
    "Name konnte nicht gespeichert werden."
  );
}

export async function updateUserName(id: number, name: string): Promise<UserNameSetting> {
  return await jsonOrError<UserNameSetting>(
    await fetch(`/api/settings/names/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    }),
    "Name konnte nicht geändert werden."
  );
}

export async function setFavoriteUserName(id: number): Promise<UserNameSetting> {
  return await jsonOrError<UserNameSetting>(
    await fetch(`/api/settings/names/${id}/favorite`, {
      method: "PUT",
      credentials: "include",
    }),
    "Favorit konnte nicht geändert werden."
  );
}

export async function deleteUserName(id: number): Promise<void> {
  const response = await fetch(`/api/settings/names/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error("Name konnte nicht gelöscht werden.");
}

export async function fetchEmailRecipients(): Promise<EmailRecipientSetting[]> {
  try {
    const recipients = await jsonOrError<EmailRecipientSetting[]>(
      await fetchWithTimeout("/api/settings/email-recipients", { credentials: "include", cache: "no-store" }, 1800),
      "E-Mail-Empfänger konnten nicht geladen werden."
    );
    cacheEmailRecipients(recipients);
    return recipients;
  } catch (error) {
    const cached = getCachedEmailRecipients();
    if (cached !== null) return cached;
    throw error;
  }
}

export async function createEmailRecipient(email: string): Promise<EmailRecipientSetting> {
  const created = await jsonOrError<EmailRecipientSetting>(
    await fetch("/api/settings/email-recipients", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }),
    "E-Mail-Empfänger konnte nicht gespeichert werden."
  );
  const cached = getCachedEmailRecipients();
  if (cached !== null) cacheEmailRecipients([...cached.filter((item) => item.id !== created.id), created]);
  return created;
}

export async function updateEmailRecipient(id: number, email: string): Promise<EmailRecipientSetting> {
  const updated = await jsonOrError<EmailRecipientSetting>(
    await fetch(`/api/settings/email-recipients/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }),
    "E-Mail-Empfänger konnte nicht geändert werden."
  );
  const cached = getCachedEmailRecipients();
  if (cached !== null) cacheEmailRecipients(cached.map((item) => item.id === updated.id ? updated : item));
  return updated;
}

export async function deleteEmailRecipient(id: number): Promise<void> {
  const response = await fetch(`/api/settings/email-recipients/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error("E-Mail-Empfänger konnte nicht gelöscht werden.");
  const cached = getCachedEmailRecipients();
  if (cached !== null) cacheEmailRecipients(cached.filter((item) => item.id !== id));
}
